import { Link, router } from '@inertiajs/react'
import { Edit, Trash2 } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { TaskTypeSummary } from '@/types'
import { DocumentUploader } from '@/components/shared/DocumentUploader'

type Template = { id: string; name: string; file_url: string | null; created_at: string }
type Props = {
  type: TaskTypeSummary
  templates: Template[]
}

export default function TaskTypesShow({ type, templates }: Props) {
  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this task type?')) {
      router.delete(`/backoffice/task_types/${type.id}`)
    }
  }

  return (
    <BackofficeLayout
      title={type.name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Task Types', href: '/backoffice/task_types' },
        { label: type.name },
      ]}
    >
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-xl font-semibold tracking-tight">{type.name}</h1>
          <Badge variant="secondary" className="font-mono text-[11px]">{type.code}</Badge>
        </div>
        {type.description && <p className="text-sm text-muted-foreground">{type.description}</p>}
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Button variant="outline" size="sm" asChild>
          <Link href={`/backoffice/task_types/${type.id}/edit`}>
            <Edit className="w-3.5 h-3.5 mr-1.5" />Edit
          </Link>
        </Button>
        <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700">
          <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
        </Button>
      </div>

      {type.fields.length > 0 && (
        <section className="border rounded-lg mb-8">
          <div className="px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">Required Fields</h2>
          </div>
          <div className="px-4 py-4">
            <div className="space-y-2">
              {type.fields.map((field) => (
                <div key={field.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0 mb-1">
                  <span>{field.label}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-[11px]">{field.field_type}</Badge>
                    {field.required && <Badge className="text-[11px]">Required</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <DocumentUploader
        title="Document Templates"
        documents={templates}
        uploadPath={`/backoffice/task_types/${type.id}/document_templates`}
        deletePath={(docId) => `/backoffice/task_types/${type.id}/document_templates/${docId}`}
      />
    </BackofficeLayout>
  )
}
